import {ActionIcon, Alert, Button, Flex, Paper, Space, Switch, Textarea} from "@mantine/core";
import {ArrowLeftIcon, CheckIcon, ArrowClockwiseIcon, XIcon, TrashIcon} from "@phosphor-icons/react";
import {DataTable} from "mantine-datatable";
import {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router";
import withRouteErrorBoundary from "../../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import {Col, Row} from "../../../components/Flex/Flex";
import Icon from "../../../components/Icon/Icon.tsx";
import IconMap from "../../../components/Icon/IconMap.ts";
import LoaderIndicator from "../../../components/Loader/LoaderIndicator.tsx";
import Page from "../../../components/Page/Page.tsx";
import MessageContext from "../../../context/MessageContext.tsx";
import CGT from "../../../utils/CGT.ts";
import {sortByName} from "../../../utils/Sorting";
import {generateSystemPlan, getDistance, relativeDirection} from "../../../utils/TravelPlanner.ts";
import {getMessage} from "../data.js";
import AddScan from "./AddScan.jsx";
import api, {useSystems, useSWCLocation} from "../../../utils/api.ts";
import FoundTarget from "./FoundTarget.jsx";
import LastSighting from "./LastSighting.jsx";
import TrackingMap from "./TrackingMap.jsx";

const scanDateBody = row => {
    if (row.date) {
        return CGT.fromSeconds(row.date).toCGT(true);
    } else {
        return '';
    }
}

function ContractDetails() {
    const navigate = useNavigate();
    const {success} = useContext(MessageContext);
    const {systems} = useSystems();
    const {state} = useLocation();
    const {id} = useParams();
    const [contract, setContract] = useState(null);
    const [notes, setNotes] = useState('');
    const [scans, setScans] = useState([]);
    const [locations, setLocations] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [networks, setNetworks] = useState([]);

    useEffect(() => {
        // load contract from backend
        api.get('/data/factions/bh').then(res => {
            setNetworks(res.data.sort(sortByName));
        })
        api.get('/bh/contract/' + id).then(res => {
            setContract(res.data);
            setNotes(res.data.notes);
            setScans(res.data.scans);
            setLocations(res.data.locations);
        }).catch((err) => {
            if (err?.response.data.message) {
                setErrorMessage(err.response.data.message);
            }
        });
    }, [id]);

    const bodySystem = useCallback(row => {
        return <ScanSystemName row={row}/>
    }, []);

    const bodyActions = useCallback(row => {
        return (
            <ActionIcon variant="light" onClick={() => api.delete('/bh/scan/' + contract.id + '/' + row.id).then((res) => {
                setScans(res.data.contract.scans);
                setLocations(res.data.contract.locations);
            })} title="Remove scan">
                <TrashIcon size={16}/>
            </ActionIcon>
        )
    }, [contract?.id]);

    const saveNote = useCallback(() => {
        api.post('/bh/contract/note', {id, notes}).then(() => {
            success('Contract note updated');
        })
    }, [id, notes]);

    const network = useMemo(() => {
        if (!contract?.network) {
            return <i>Not selected</i>;
        }
        let n = networks.find(n => n.id === contract?.network);
        return n ? n.name : <i>Dissolved</i>
    }, [contract?.network, networks]);

    const expireDuration = useMemo(() => {
        if (!contract) {
            return '';
        }
        let now = CGT.now();
        let date = CGT.fromCGT(contract.expiration_y, contract.expiration_d);
        return <span className={date < now ? 'text-red' : ''}>
            <span>{date.toCGT(true)}</span> (<small>{date.toRelative()}</small>)
        </span>;
    }, [contract?.expiration_y, contract?.expiration_d]);


    if (errorMessage !== '') {
        return <Page><Alert color="red">{errorMessage}</Alert></Page>
    }
    if (contract === null) {
        return <Page><LoaderIndicator id="load-contract" message="Loading contract data"/></Page>
    }

    return (
        <Page>
            <title>{contract ? 'Contract - ' + contract.name : 'Contract'}</title>
            <Row mb={2} wrap={false}>
                <Col>
                    <Row>
                        <Button leftSection={<ArrowLeftIcon size={16}/>} onClick={() => {
                            if (state?.prev) {
                                navigate(-1);
                            } else {
                                navigate('/bh/contracts');
                            }
                        }}>Back</Button>
                        <Button leftSection={<ArrowClockwiseIcon size={16}/>} onClick={() => {
                            api.get('/bh/contract/' + contract.id + '/recalculate').then(res => {
                                success('Calculations complete', 'Possible locations have been refreshed');
                                setLocations(res.data.contract.locations);
                            })
                        }} title="Calculate the possible target locations">Recalculate</Button>
                    </Row>
                </Col>
                <Col grow={1}/>
                <Col>

                </Col>
                <Col>
                    <Row justify="end">
                        <Button leftSection={<CheckIcon size={16}/>} color="green" onClick={() => {
                            api.post('/bh/contract/' + contract.id + '/complete').then(() => {
                                success('Contract completed', getMessage('success'));
                                navigate('/bh/contracts');
                            })
                        }}>Mark Complete</Button>
                        <Button leftSection={<XIcon size={16}/>} color="red" onClick={() => {
                            api.post('/bh/contract/' + contract.id + '/fail').then(() => {
                                success('Contract failed', getMessage('fail'));
                                navigate('/bh/contracts');
                            })
                        }}>Mark Failed</Button>

                    </Row>
                </Col>
            </Row>
            <Row mb={1}>
                <Col grow={1}>
                    <h2 className="mt-0 mb-1">{contract.name}</h2>
                    <Flex gap="lg">
                        <Col>
                            Difficulty: {contract.difficulty}<br/>
                            Contract type: {contract.type}<br/>
                            Reward type: {contract.reward}<br/>
                        </Col>
                        <Col>
                            Network: {network}<br/>
                            Expiration: {expireDuration}
                        </Col>
                    </Flex>

                </Col>
                <Col grow={1}>
                    <Textarea className="w-full" value={notes} onChange={e => setNotes(e.target.value)} onBlur={saveNote} placeholder="Contract notes"/>
                </Col>
            </Row>

            <Paper>
                <DataTable
                    records={scans}
                    idAccessor="id"
                    fz="sm"
                    columns={[
                        {accessor: 'system', title: 'System', render: (row) => bodySystem(row)},
                        {accessor: 'date', title: 'Scan date', render: (row) => scanDateBody(row), sortable: true},
                        {accessor: 'direction', title: 'Direction'},
                        {accessor: 'distance', title: 'Distance'},
                        {accessor: 'actions', title: 'Actions', render: (row) => bodyActions(row)},
                    ]}
                />
            </Paper>
            <Space h="md"/>

            <AddScan contractId={contract.id} systems={systems} onSave={(contract) => {
                setScans(contract.scans);
                setLocations(contract.locations);
            }}/>
            <Space h="sm"/>

            {locations.length === 1 ? <FoundTarget location={locations[0]}/> : null}
            {contract.sighting ? <LastSighting sighting={contract.sighting}/> : null}
            <Space h="sm"/>

            {scans.length > 0 || contract.sighting ? (<Locations locations={locations} scans={scans} sighting={contract.sighting}/>) : null}

        </Page>
    );
}

const sortLocationsDistance = (a, b) => a.distance - b.distance;

const ScanSystemName = ({row}) => {
    const {systems, isPending} = useSystems();
    const name = useMemo(() => {
        if (!row.system) {
            return 'Deep Space';
        }

        let system = systems.find(s => s.id === row.system);
        if (system) {
            return system.name;
        }

        return "Loading...";
    }, [row.system, systems]);

    if (isPending) {
        return null;
    }
    return <span>{name} ({row.x}, {row.y})</span>
}

const Locations = ({locations, scans, sighting}) => {
    const {location} = useSWCLocation();
    const [showScanLines, setShowScanLines] = useState(!sighting && locations.length > 1);
    const displayLocations = useMemo(() => {
        if (scans.length === 0) {
            return locations.sort(sortByName);
        }
        const lastScan = scans[scans.length - 1];

        return locations.map(l => {
            l = {...l};
            l.distance = Math.max(Math.abs(l.galX - lastScan.x), Math.abs(l.galY - lastScan.y));
            return l;
        }).sort(sortLocationsDistance);
    }, [locations, scans]);

    return (
        <Paper>
            <Row justify="between" mb={2}>
                <Col>
                    {locations.length === 0 && !sighting ? <i>No possible locations found, double check your scans</i> :
                        <h2 className="m-0">Possible locations: {locations.length}</h2>}
                </Col>
                <Col>
                    {location ?
                        <Switch checked={showScanLines} onChange={e => setShowScanLines(e.currentTarget.checked)} label="Show current scan lines"/> : null}
                </Col>
            </Row>


            <Row className="flex-column md:flex-row" align="stretch">
                <TrackingMap locations={locations} sighting={sighting} scans={scans} currentLocation={location} showCurrentLines={showScanLines}/>
                <Col grow={1} shrink={0} basis="200px">
                    {sighting ?
                        <>
                            <h3 className="mt-0">Last sighting</h3>
                            <PossibleLocation system={sighting.location.system} type="sighting"/>
                        </> : null}
                    <h3 className="mt-1">Possible locations</h3>
                    {displayLocations.map(l => <PossibleLocation key={l.id} system={l} type="target"/>)}
                </Col>
            </Row>
        </Paper>
    )
}


const PossibleLocation = ({system}) => {
    let nid = system.id.substring(2);
    const {location} = useSWCLocation();
    let direction;
    if (location) {
        const distance = getDistance(location.coordinates.galaxy.attributes.x, location.coordinates.galaxy.attributes.y, system.galX, system.galY);
        direction =
            <span> - {relativeDirection(location.coordinates.galaxy.attributes.x, location.coordinates.galaxy.attributes.y, system.galX, system.galY)} <span
                title="Distance from current location">[{distance}]</span></span>;
    }
    return (
        <>
            <Row>
                <Col grow={1}>
                    <b>{system.name}</b><br/>
                </Col>
                <Col>
                    <Flex gap="xs">
                        <a href={`https://www.swcombine.com/rules/?Galaxy_Map&systemID=${nid}`}
                           target="_blank" className={`travel-planner ml-3`} title="System details" rel="noreferrer"><Icon icon={IconMap.SwcRules}/></a>
                        <a href={generateSystemPlan(system.galX, system.galY, location)}
                           target="_blank" className={`travel-planner ml-3`} title="Travel Planner" rel="noreferrer"><Icon icon={IconMap.TravelPlanner}/></a>
                    </Flex>
                </Col>
            </Row>
            <Row mb={3}>
                <Col grow={1}>
                    <small>({system.galX}, {system.galY}){direction}</small>
                </Col>
                <Col>
                    <small title="Distance from last scan">{system.distance}</small>
                </Col>
            </Row>
        </>
    )
}
export default withRouteErrorBoundary(ContractDetails);
